import React from 'react';
import { useParams } from 'react-router-dom';
import { useGetHeatsToRegister, useGetPlayerSummary } from '../hooks/tournamentHooks';
import { useTranslation } from 'react-i18next';
import type { Heat, HeatRegistrationSummary } from '@iziping/common';
import PlayerCard from '../../../components/shared/PlayerCard';
import { useSearchPlayerMatches } from '@/components/matches/matchesHooks';
import { QUERY_CACHE } from '@/model/endpoints';
import { useCreateHeatRegistration, useRemoveHeatRegistration } from '@/components/heat/heatHooks';
import { useQueryClient } from '@tanstack/react-query';
import { useNotification } from '@iziping/ui';
import { UserMinus, UserPlus } from 'lucide-react';
import PlayerMatchHistory from '../components/PlayerMatchHistory';
import styles from './PlayerTournamentDetail.module.css';

const PlayerTournamentDetail: React.FC = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { notify } = useNotification();
    const { id } = useParams();
    const { playerId } = useParams();
    const { data: playerDetail, isLoading, isError } = useGetPlayerSummary(Number(id), playerId || '');
    const { data: playerMatches } = useSearchPlayerMatches({ tournamentId: Number(id), playerId: playerId ?? '' });
    const { data: heatsToRegister } = useGetHeatsToRegister({ tournamentId: Number(id), playerId: playerId ?? '' });
    const addHeatRegistration = useCreateHeatRegistration();
    const removeHeatRegistration = useRemoveHeatRegistration();

    const handleAddHeatRegistration = async (heatId: number) => {
        try {
            await addHeatRegistration.mutateAsync({ heatId: heatId ?? '', players: [playerDetail?.player] });
            await queryClient.invalidateQueries({ queryKey: [QUERY_CACHE.PLAYER_SUMMARY] });
            notify(t('heat.player.registered'), 'success');
        } catch (error: unknown) {
            notify(t((error as { error: string }).error), 'error');
        }
    };

    const handleRemoveHeatRegistration = async (heatId: number, registrationId: number) => {
        try {
            await removeHeatRegistration.mutateAsync({ id: heatId ?? '', registrationId: registrationId });
            await queryClient.invalidateQueries({ queryKey: [QUERY_CACHE.PLAYER_SUMMARY] });
            notify(t('heat.player.unregistered'), 'success');
        } catch (error: unknown) {
            notify(t((error as { error: string }).error), 'error');
        }
    };

    if (isLoading) return <div>Loading...</div>;
    if (isError) return <div>Error fetching data</div>;

    return (
        <div className={'page'}>
            <div className={'container-column'}>
                <div className={'size-1'} style={{ paddingTop: '1.5rem' }}>
                    <PlayerCard player={playerDetail!.player} />
                </div>
                <div className={'size-2'}>
                    <h3>Liste des séries du tournoi</h3>
                    <table className={'table-stripped'}>
                        <thead>
                            <tr>
                                <th>{t('HEAT_NAME')}</th>
                                <th>{t('HEAT_TYPE')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {heatsToRegister?.map((heat: Heat) => (
                                <tr key={heat.id}>
                                    <td>{heat.name}</td>
                                    <td>{t(`HEATTYPE.${heat.type}`)}</td>
                                    <td>
                                        <UserPlus
                                            className={styles.actionIcon}
                                            fill="currentColor"
                                            size={20}
                                            onClick={() => handleAddHeatRegistration(heat.id)}
                                            aria-label={`Inscrire à ${heat.name}`}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <h3>{t('HEAT_PLAYER_ALREADY_REGISTERED')}</h3>
                    <table className={'table-stripped'}>
                        <thead>
                            <tr>
                                <th>{t('HEAT_NAME')}</th>
                                <th>{t('HEAT_TYPE')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {playerDetail?.heatRegistrations?.map((registration: HeatRegistrationSummary) => (
                                <tr key={registration.id}>
                                    <td>{registration.heat.name}</td>
                                    <td>{t(`HEATTYPE.${registration.heat.type}`)}</td>
                                    <td>
                                        <UserMinus
                                            className={styles.actionIcon}
                                            fill="currentColor"
                                            size={20}
                                            onClick={() =>
                                                handleRemoveHeatRegistration(registration.heat.id, registration.id)
                                            }
                                            aria-label={`Désinscrire de ${registration.heat.name}`}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className={styles.matchSection}>
                <h3 className={styles.sectionTitle}>Historique des matchs</h3>
                <PlayerMatchHistory matches={playerMatches ?? []} playerId={Number(playerId)} />
            </div>
        </div>
    );
};

export default PlayerTournamentDetail;
